import { createContext, useContext, useState, useEffect, type ReactNode } from 'react';
import { onAuthStateChanged, signOut, type Auth } from 'firebase/auth';
import { useNavigate } from 'react-router-dom';

import { getAuthUser, useAccessToken } from '@flintmint/web-shared/hooks/use-auth-user';
import { authDenialReasons, type AuthDenialReason } from '@flintmint/core-shared/constants';

import { ENVIRONMENT, apiRoutes, appRoutes } from '@/constants';
import { authenticateWithToken, getFirebaseAuth } from '@/services/firebase-service';

interface FirebaseAuthState {
  isAuthenticated: boolean;
  isAdmin: boolean;
  isLoading: boolean;
  error: string | null;
  denialReason: AuthDenialReason | null;
}

const FirebaseAuthContext = createContext<FirebaseAuthState>({
  isAuthenticated: false,
  isAdmin: false,
  isLoading: true,
  error: null,
  denialReason: null,
});

export function FirebaseAuthProvider({ children }: { children: ReactNode }) {
  const accessToken = useAccessToken();
  const navigate = useNavigate();
  const [state, setState] = useState<FirebaseAuthState>({
    isAuthenticated: false,
    isAdmin: false,
    isLoading: true,
    error: null,
    denialReason: null,
  });

  useEffect(() => {
    if (!accessToken) {
      setState({ isAuthenticated: false, isAdmin: false, isLoading: false, error: null, denialReason: null });
      return;
    }

    let superseded = false;

    async function authenticate(accessToken: string) {
      try {
        setState((prev) => ({ ...prev, isLoading: true, error: null, denialReason: null }));

        const auth = getFirebaseAuth();
        await auth.authStateReady();

        const userId = getAuthUser().id;
        const reusable = await hasPersistedAdminSession(auth, userId);
        if (superseded) return;
        if (reusable) {
          setState({ isAuthenticated: true, isAdmin: true, isLoading: false, error: null, denialReason: null });
          refreshAccessInBackground(accessToken, auth, () => superseded);
          return;
        }

        // Auth state is shared across tabs: signing out a same-user session would log out every
        // other open admin tab, and signInWithCustomToken replaces it in place.
        if (auth.currentUser && auth.currentUser.uid !== userId) await signOut(auth);

        const result = await requestFirebaseToken(accessToken);
        if (superseded) return;
        if ('denialReason' in result) {
          setState({
            isAuthenticated: false,
            isAdmin: false,
            isLoading: false,
            error: null,
            denialReason: result.denialReason,
          });
          return;
        }

        const credential = await authenticateWithToken(result.token);
        const tokenResult = await credential.user.getIdTokenResult();
        if (superseded) return;

        setState({
          isAuthenticated: true,
          isAdmin: tokenResult.claims.isAdmin === true,
          isLoading: false,
          error: null,
          denialReason: null,
        });
      } catch (error) {
        console.error('Firebase authentication error:', error);
        if (superseded) return;
        setState({
          isAuthenticated: false,
          isAdmin: false,
          isLoading: false,
          error: error instanceof Error ? error.message : 'Authentication failed',
          denialReason: null,
        });
      }
    }

    authenticate(accessToken);

    return () => {
      superseded = true;
    };
  }, [accessToken]);

  useEffect(() => {
    if (!state.isAuthenticated) return;

    // A background denial or a revoked refresh token signs the SDK out; the logout route clears Auth0 and local state.
    return onAuthStateChanged(getFirebaseAuth(), (user) => {
      // Logout's own signOut fires this while already on the logout route.
      if (!user && window.location.pathname !== appRoutes.logout) navigate(appRoutes.logout);
    });
  }, [state.isAuthenticated, navigate]);

  return <FirebaseAuthContext.Provider value={state}>{children}</FirebaseAuthContext.Provider>;
}

export function useFirebaseAuth(): FirebaseAuthState {
  return useContext(FirebaseAuthContext);
}

// Reusing a session is only safe when it carries the `isAdmin` claim firestore.rules gate admin
// reads and writes on - it is minted solely into the custom token at the /api/auth exchange.
async function hasPersistedAdminSession(auth: Auth, userId: string): Promise<boolean> {
  const user = auth.currentUser;
  if (!userId || user?.uid !== userId) return false;

  try {
    const { claims } = await user.getIdTokenResult();
    return claims.isAdmin === true;
  } catch {
    // The admin app has no offline boot, so a session whose isAdmin claim cannot be read is
    // re-minted through the token exchange instead of trusted.
    return false;
  }
}

// A persisted Firebase session proves a past admission, not a current one: re-run the server
// gate off the critical path and, on a denial, sign the session out - the auth-state listener
// above then runs the full logout flow.
function refreshAccessInBackground(accessToken: string, auth: Auth, isSuperseded: () => boolean) {
  requestAccessCheck(accessToken)
    .then((denialReason) => {
      if (denialReason && !isSuperseded()) return signOut(auth);
    })
    .catch((error) => console.warn('Background access check failed:', error));
}

async function requestAccessCheck(accessToken: string): Promise<AuthDenialReason | null> {
  const result = await fetchApi(apiRoutes.accessCheck, accessToken, 'Access check failed');
  return 'denialReason' in result ? result.denialReason : null;
}

async function requestFirebaseToken(accessToken: string): Promise<{ token: string } | { denialReason: AuthDenialReason }> {
  const result = await fetchApi(apiRoutes.firebaseAuth, accessToken, 'Failed to get Firebase token');
  if ('denialReason' in result) return result;

  const { token } = await result.json();
  return { token };
}

async function fetchApi(
  path: string,
  accessToken: string,
  failureMessage: string
): Promise<Response | { denialReason: AuthDenialReason }> {
  const response = await fetch(`${ENVIRONMENT.VITE_API_HOST}/api${path}`, {
    headers: {
      Authorization: `Bearer ${accessToken}`,
    },
  });

  if (response.ok) return response;

  if (response.status === 403) {
    const body = (await response.json().catch(() => null)) as { error?: string } | null;
    const denialReason = authDenialReasons.find((r) => r === body?.error);
    if (denialReason) return { denialReason };
  }
  throw new Error(`${failureMessage}: ${response.status}`);
}
